import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PhotoUploader } from './PhotoUploader';
import { EvidenceRequirements } from '../common/BulletList';
import { entryMatchesUnit } from '../../lib/helpers';
import { supabase } from '../../lib/supabase';

// Staff who may correct an entry without it bouncing back through review
const STAFF_EDITORS = ['trainer', 'employer'];

// ── Local backup ────────────────────────────────────────────────────────────
// Draft saves used to fail silently against the server, which cost apprentices a
// whole entry. Everything typed is now mirrored to this device as well, so a failed
// save, a closed tab or a flat battery can't lose the work.
const LOCAL_PREFIX = 'signedoff.evidence.';
const localKey = (profileId, id) => `${LOCAL_PREFIX}${profileId}.${id || 'new'}`;

function readLocal(profileId, id) {
  try {
    const raw = localStorage.getItem(localKey(profileId, id));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeLocal(profileId, id, data) {
  try { localStorage.setItem(localKey(profileId, id), JSON.stringify(data)); } catch {}
}
function clearLocal(profileId, id) {
  try { localStorage.removeItem(localKey(profileId, id)); } catch {}
}

/**
 * Pull the three structured fields back out of a saved `what_doing` blob.
 * Legacy entries have no labels at all — treat the whole thing as the work
 * description rather than dropping it on the floor.
 */
export function parseWhatDoing(text) {
  const out = { fault: '', work: '', tools: '' };
  if (!text) return out;

  const LABELS = [
    ['Fault/Reason:', 'fault'],
    ['Work performed:', 'work'],
    ['Tools/Equipment:', 'tools'],
  ];
  const found = [];
  for (const [label, key] of LABELS) {
    const at = text.indexOf(label);
    if (at !== -1) found.push({ at, key, len: label.length });
  }
  if (found.length === 0) { out.work = text.trim(); return out; }

  found.sort((a, b) => a.at - b.at);
  found.forEach((f, i) => {
    const end = i + 1 < found.length ? found[i + 1].at : text.length;
    out[f.key] = text.slice(f.at + f.len, end).trim();
  });
  return out;
}

export function AddEvidenceModal({ profile, units, evidence, trainingPlans, activeApprId, onClose, onSave, onSaveDraft, onUpdateDraft, editingDraft, presetUnitCode }) {
  // If resuming a draft or editing a submitted entry, pre-fill from it
  const draft = editingDraft || null;
  // Editing a submitted entry (pending/approved/rejected) vs a draft
  const isEditingSubmitted = draft && draft.status && draft.status !== 'draft';
  // A trainer/employer correcting someone's entry shouldn't unapprove it
  const isStaffEdit = isEditingSubmitted && STAFF_EDITORS.includes(profile.role);
  const parsed = parseWhatDoing(draft?.what_doing);

  const [draftId, setDraftId]           = useState(draft?.id || null);
  const [unitCodes, setUnitCodes]       = useState(() => {
    if (draft?.unit_codes) { try { return JSON.parse(draft.unit_codes); } catch {} }
    if (draft?.unit_code) return [draft.unit_code];
    // Started from a unit on the dashboard — that unit is already chosen
    return presetUnitCode ? [presetUnitCode] : [];
  });
  const [jobNumber, setJobNumber]       = useState(draft?.job_number || '');
  const [vehicleRego, setVehicleRego]   = useState(draft?.vehicle_rego || '');
  const [date, setDate]                 = useState(draft?.date || new Date().toISOString().slice(0, 10));
  const [faultReason, setFaultReason]   = useState(draft?.fault_reason || parsed.fault);
  const [whatDid, setWhatDid]           = useState(parsed.work);
  const [toolsUsed, setToolsUsed]      = useState(draft?.tools_used || parsed.tools);
  const [outcome, setOutcome]           = useState(draft?.outcome || draft?.why_doing || '');
  const [photoList, setPhotoList]       = useState(() => {
    if (draft?.photo_urls) { try { return JSON.parse(draft.photo_urls); } catch {} }
    return [draft?.photo_fault_url, draft?.photo_repair_url, draft?.photo_completed_url, draft?.photo_additional_url].filter(Boolean);
  });
  const [previewList, setPreviewList]   = useState([]);

  const [saving, setSaving]             = useState(false);
  const [draftSaving, setDraftSaving]   = useState(false);
  const [draftSaved, setDraftSaved]     = useState(!!draft);
  const [draftError, setDraftError]     = useState('');
  const [backup, setBackup]             = useState(null); // unsaved work found on this device
  const [err, setErr]                   = useState('');
  const [showReqs, setShowReqs]         = useState(null);
  const [unitSearch, setUnitSearch]     = useState('');

  const allUnits = units || [];
  // Whose entry this is — the apprentice themselves, or the student whose entry a
  // trainer/employer has opened. Progress counts and plan filtering follow them, not
  // the person doing the typing.
  const apprenticeId = draft?.apprentice_id || (profile.role === 'apprentice' ? profile.id : activeApprId);
  const myEvidence = (evidence || []).filter(e => e.apprentice_id === apprenticeId);
  const autoSaveTimer = useRef(null);
  const hasContent = useRef(false);

  // ── Restrict apprentices to units on their training plan ──
  // Trainers/employers/supervisors see everything.
  // Apprentices see only plan units, plus any unit already selected on the current entry
  // (so editing a saved/draft entry referencing an off-plan unit still works).
  const planCodes = (trainingPlans || [])
    .filter(p => p.apprentice_id === apprenticeId)
    .map(p => p.unit_code);
  const apprenticeHasPlan = profile.role === 'apprentice' && planCodes.length > 0;

  const visibleUnits = (() => {
    if (profile.role !== 'apprentice') return allUnits;
    if (planCodes.length === 0) return allUnits; // no plan loaded — show all rather than lock them out
    const allowed = new Set([...planCodes, ...unitCodes]); // include already-selected codes for editing
    return allUnits.filter(u => allowed.has(u.code));
  })();

  // ── Filter units by search (exclude already-selected, they show separately at top) ──
  const filteredUnits = visibleUnits.filter(u => {
    if (unitCodes.includes(u.code)) return false; // already selected — shown at top
    if (!unitSearch.trim()) return true; // no search — show all
    const q = unitSearch.toLowerCase();
    return u.code.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
  });

  // ── Check if form has enough content to warrant a draft save ──
  function hasDraftableContent() {
    return jobNumber.trim() || faultReason.trim() || whatDid.trim() || outcome.trim() || photoList.length > 0;
  }

  // ── Build the draft/entry payload ──
  function buildPayload(status) {
    return {
      // Editing an existing entry must never reassign it — a trainer correcting a
      // student's entry would otherwise file it against themselves.
      apprentice_id: draft?.apprentice_id || profile.id,
      unit_code: unitCodes[0] || null,
      unit_codes: unitCodes.length > 0 ? JSON.stringify(unitCodes) : null,
      job_number: jobNumber.trim() || null,
      vehicle_rego: vehicleRego.trim() || null,
      date,
      what_doing: buildWhatDoing(),
      why_doing: buildWhyDoing(),
      fault_reason: faultReason.trim() || null,
      tools_used: toolsUsed.trim() || null,
      outcome: outcome.trim() || null,
      photo_fault_url: photoList[0] || null,
      photo_repair_url: photoList[1] || null,
      photo_completed_url: photoList[2] || null,
      photo_additional_url: photoList[3] || null,
      photo_urls: photoList.length > 0 ? JSON.stringify(photoList) : null,
      status,
    };
  }

  // ── The form as a plain object, for the on-device backup ──
  function snapshot() {
    return {
      savedAt: new Date().toISOString(),
      unitCodes, jobNumber, vehicleRego, date,
      faultReason, whatDid, toolsUsed, outcome, photoList,
    };
  }

  function applySnapshot(s) {
    setUnitCodes(s.unitCodes || []);
    setJobNumber(s.jobNumber || '');
    setVehicleRego(s.vehicleRego || '');
    setDate(s.date || new Date().toISOString().slice(0, 10));
    setFaultReason(s.faultReason || '');
    setWhatDid(s.whatDid || '');
    setToolsUsed(s.toolsUsed || '');
    setOutcome(s.outcome || '');
    setPhotoList(s.photoList || []);
  }

  // ── Auto-save draft logic ──
  const saveDraft = useCallback(async () => {
    if (!hasDraftableContent()) return true;
    setDraftSaving(true);
    try {
      if (draftId && onUpdateDraft) {
        // Update existing draft
        await onUpdateDraft(draftId, buildPayload('draft'));
      } else if (onSaveDraft) {
        // Create new draft
        const newDraft = await onSaveDraft(buildPayload('draft'));
        if (newDraft?.id) {
          setDraftId(newDraft.id);
          clearLocal(profile.id, null); // backup now lives under the draft's id
        }
      }
      setDraftSaved(true);
      setDraftError('');
      setDraftSaving(false);
      return true;
    } catch (e) {
      // Never swallow this: the apprentice needs to know the server copy didn't
      // land, and that their work is still held on this device.
      console.warn('Draft auto-save failed:', e.message);
      setDraftSaved(false);
      setDraftError(e.message || 'Could not save draft to the server.');
      setDraftSaving(false);
      return false;
    }
  }, [draftId, unitCodes, jobNumber, vehicleRego, date, faultReason, whatDid, toolsUsed, outcome, photoList]);

  // ── Mirror every change to this device immediately (survives crashes/closed tabs) ──
  useEffect(() => {
    if (!hasDraftableContent()) return;
    writeLocal(profile.id, draftId, snapshot());
  }, [unitCodes, jobNumber, vehicleRego, date, faultReason, whatDid, toolsUsed, outcome, photoList, draftId, profile.id]);

  // ── Offer back any work this device kept from a previous session ──
  useEffect(() => {
    const saved = readLocal(profile.id, draft?.id || null);
    if (!saved) return;
    if (!draft) {
      // Brand new entry — nothing to conflict with, restore straight away
      applySnapshot(saved);
      setBackup({ ...saved, restored: true });
    } else if (draft.updated_at && saved.savedAt > draft.updated_at) {
      // Local copy is newer than the server's — let them choose
      setBackup(saved);
    } else {
      clearLocal(profile.id, draft.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Show existing photos when resuming a draft or editing an entry ──
  // Without this the uploader looks empty and apprentices re-shoot work they'd
  // already attached. Any slot without a thumbnail gets a signed URL.
  useEffect(() => {
    const missing = photoList
      .map((p, i) => ({ p, i }))
      .filter(({ p, i }) => p && !previewList[i] && !p.startsWith('data:'));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const paths = missing.map(m => m.p).filter(p => !p.startsWith('http'));
      const urlByPath = {};
      if (paths.length > 0) {
        const { data } = await supabase.storage.from('evidence-photos').createSignedUrls(paths, 60 * 60);
        (data || []).forEach((row, i) => { if (row?.signedUrl) urlByPath[paths[i]] = row.signedUrl; });
      }
      if (cancelled) return;
      setPreviewList(prev =>
        photoList.map((p, i) => prev[i] || (p ? (p.startsWith('http') ? p : urlByPath[p] || null) : null))
      );
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoList]);

  // ── Trigger auto-save when form changes (only for new entries and drafts, not submitted edits) ──
  useEffect(() => {
    if (isEditingSubmitted) return; // Don't auto-save drafts when editing submitted entries
    if (!hasDraftableContent()) return;
    hasContent.current = true;

    // Clear any existing timer
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    // Debounce: save draft 2 seconds after last change
    autoSaveTimer.current = setTimeout(() => {
      saveDraft();
    }, 2000);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [unitCodes, jobNumber, vehicleRego, date, faultReason, whatDid, toolsUsed, outcome, photoList]);

  // ── Save draft on close if there's content (not for submitted edits) ──
  // The on-device copy is already written, so closing can't lose anything even if
  // this last server save fails.
  async function handleClose() {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    if (!isEditingSubmitted && hasContent.current && hasDraftableContent()) {
      const ok = await saveDraft();
      // Stay open on failure so the error is visible and they can retry —
      // silently closing is exactly how entries used to disappear.
      if (!ok) return;
    }
    clearLocal(profile.id, draftId);
    clearLocal(profile.id, null);
    onClose();
  }

  // Escape hatch when the server won't accept the draft — the work stays on this
  // device and reappears next time they open the form.
  function closeKeepingLocal() {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    writeLocal(profile.id, draftId, snapshot());
    onClose();
  }

  function toggleUnit(code) {
    setUnitCodes(prev => {
      const isAdding = !prev.includes(code);
      const next = isAdding ? [...prev, code] : prev.filter(c => c !== code);
      if (isAdding) {
        const u = allUnits.find(u => u.code === code);
        if (u && (u.evidence_requirements || u.photo_guide || u.write_guide || u.example_entry || u.workshop_tip)) {
          setShowReqs(code);
        }
      } else if (showReqs === code) {
        setShowReqs(null);
      }
      return next;
    });
  }

  function getUnitProgress(code) {
    const u = allUnits.find(u => u.code === code);
    const minEv = u?.min_evidence || 3;
    const approved = myEvidence.filter(e => e.status === 'approved' && entryMatchesUnit(e, code)).length;
    const pending = myEvidence.filter(e => e.status === 'pending' && entryMatchesUnit(e, code)).length;
    return { approved, pending, minEv, met: approved >= minEv };
  }

  function buildWhatDoing() {
    let parts = [];
    if (faultReason.trim()) parts.push(`Fault/Reason: ${faultReason.trim()}`);
    if (whatDid.trim()) parts.push(`Work performed: ${whatDid.trim()}`);
    if (toolsUsed.trim()) parts.push(`Tools/Equipment: ${toolsUsed.trim()}`);
    return parts.join('\n\n');
  }

  function buildWhyDoing() {
    return outcome.trim() || '';
  }

  // ── Full submit ──
  async function save() {
    if (unitCodes.length === 0) return setErr('Please select at least one unit.');
    if (!jobNumber.trim()) return setErr('Please enter a job number.');
    if (!faultReason.trim()) return setErr('Please describe the fault or reason for the work.');
    if (!whatDid.trim()) return setErr('Please describe what you did.');
    if (!outcome.trim()) return setErr('Please describe the outcome.');
    if (photoList.length === 0) return setErr('Please upload at least one photo or video.');

    // Clear any pending auto-save
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    setSaving(true);
    setErr('');

    // A student's edit goes back for review; a trainer/employer correcting an entry
    // leaves the status where it was — they're the reviewer.
    const newStatus = isStaffEdit ? draft.status : 'pending';

    try {
      if (draftId && onUpdateDraft) {
        // Editing an existing entry (draft or submitted) — update the row
        await onUpdateDraft(draftId, buildPayload(newStatus));
      } else {
        // Create as pending directly
        await onSave(buildPayload('pending'));
      }
      clearLocal(profile.id, draftId);
      clearLocal(profile.id, null);
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed');
    }
    setSaving(false);
  }

  return (
    <div className="ev-form-backdrop" onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="ev-form-panel">
        {/* Close button for full-page mode */}
        <button className="ev-form-close" onClick={handleClose} title="Close">×</button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div className="modal-title" style={{ marginBottom: 0 }}>
            {isEditingSubmitted ? 'Edit Evidence' : draftId ? 'Edit Draft Evidence' : 'Log Workplace Evidence'}
          </div>
          {/* Draft save indicator */}
          <div style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            {!isEditingSubmitted && draftSaving && (
              <><span className="spin" style={{ width: 12, height: 12, borderWidth: 2 }} /> Saving draft…</>
            )}
            {!isEditingSubmitted && !draftSaving && draftSaved && !draftError && (
              <span style={{ color: 'var(--green)' }}>✓ Draft saved</span>
            )}
            {!isEditingSubmitted && !draftSaving && draftError && (
              <span style={{ color: 'var(--red)' }}>⚠ Draft not saved to server</span>
            )}
          </div>
        </div>

        {/* Draft save failed — tell them plainly, and make clear nothing is lost */}
        {draftError && !isEditingSubmitted && (
          <div style={{ padding: '10px 12px', borderRadius: 6, background: 'rgba(192,57,43,.08)', border: '1px solid rgba(192,57,43,.35)', fontSize: 11.5, color: 'var(--text)', marginBottom: 14 }}>
            <div style={{ fontWeight: 700, color: 'var(--red)', marginBottom: 4 }}>Couldn't save your draft to the server</div>
            <div style={{ marginBottom: 8 }}>
              Your work is saved on this device and will come back when you reopen this
              form on the same phone or computer. {draftError}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn-sm" style={{ fontSize: 11 }} onClick={() => saveDraft()} disabled={draftSaving}>
                {draftSaving ? 'Retrying…' : 'Try again'}
              </button>
              <button className="btn-sm" style={{ fontSize: 11 }} onClick={closeKeepingLocal}>
                Close and keep on this device
              </button>
            </div>
          </div>
        )}

        {/* Work recovered from this device */}
        {backup && (
          <div style={{ padding: '10px 12px', borderRadius: 6, background: 'rgba(39,174,96,.08)', border: '1px solid rgba(39,174,96,.3)', fontSize: 11.5, color: 'var(--text)', marginBottom: 14 }}>
            {backup.restored ? (
              <>
                <strong>Restored your unsaved work</strong> from {new Date(backup.savedAt).toLocaleString()}.{' '}
                <button
                  className="btn-sm"
                  style={{ fontSize: 10, marginLeft: 4 }}
                  onClick={() => { clearLocal(profile.id, draftId); applySnapshot({}); setBackup(null); }}
                >
                  Start fresh instead
                </button>
              </>
            ) : (
              <>
                <strong>This device has newer unsaved changes</strong> from {new Date(backup.savedAt).toLocaleString()}.{' '}
                <button
                  className="btn-sm"
                  style={{ fontSize: 10, marginRight: 4 }}
                  onClick={() => { applySnapshot(backup); setBackup(null); }}
                >
                  Restore them
                </button>
                <button
                  className="btn-sm"
                  style={{ fontSize: 10 }}
                  onClick={() => { clearLocal(profile.id, draft?.id || null); setBackup(null); }}
                >
                  Discard them
                </button>
              </>
            )}
          </div>
        )}

        {/* Warning when editing a submitted entry */}
        {isEditingSubmitted && (
          <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(245,166,35,.1)', border: '1px solid rgba(245,166,35,.3)', fontSize: 11, color: '#92400e', marginBottom: 14 }}>
            {isStaffEdit ? (
              <>✏️ You're editing {draft.status === 'approved' ? 'an approved' : `a ${draft.status}`} entry as {profile.role === 'trainer' ? 'the trainer' : 'the employer'}. Saving keeps it <strong>{draft.status}</strong> — it won't go back into the review queue.</>
            ) : (
              <>⚠️ You're editing a <strong>{draft.status}</strong> entry. Saving will resubmit it for trainer review.</>
            )}
          </div>
        )}

        {/* ── STEP 1: Job details ── */}
        <div style={{ padding: '10px 14px', borderRadius: 6, background: 'var(--panel)', border: '1px solid var(--border)', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, letterSpacing: 0.5 }}>STEP 1 — JOB DETAILS</div>
          <div className="frow">
            <div className="fgrp" style={{ marginBottom: 0 }}>
              <label className="flbl">Job Number *</label>
              <input className="finp" placeholder="e.g. JOB-2458" value={jobNumber} onChange={e => setJobNumber(e.target.value)} />
            </div>
            <div className="fgrp" style={{ marginBottom: 0 }}>
              <label className="flbl">Vehicle / Rego</label>
              <input className="finp" placeholder="e.g. ABC-123 or 2019 Volvo FH" value={vehicleRego} onChange={e => setVehicleRego(e.target.value)} />
            </div>
          </div>
          <div className="fgrp" style={{ marginTop: 10, marginBottom: 0 }}>
            <label className="flbl">Date of Work *</label>
            <input className="finp" type="date" value={date} onChange={e => setDate(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
        </div>

        {/* ── STEP 2: Unit selection ── */}
        <div style={{ padding: '10px 14px', borderRadius: 6, background: 'var(--panel)', border: '1px solid var(--border)', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, letterSpacing: 0.5 }}>STEP 2 — SELECT UNIT(S)</div>

          {unitCodes.length === 0 && apprenticeHasPlan && (
            <div style={{ padding: '6px 10px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 11, color: '#1e40af', marginBottom: 8 }}>
              💡 Showing units from your training plan. Could your evidence apply to multiple units? Select all relevant ones to get the most out of each entry.
            </div>
          )}

          {unitCodes.length === 0 && !apprenticeHasPlan && profile.role === 'apprentice' && (
            <div style={{ padding: '6px 10px', borderRadius: 6, background: '#fef3c7', border: '1px solid #fde68a', fontSize: 11, color: '#92400e', marginBottom: 8 }}>
              ⚠ No training plan found for your account — showing all units. Ask your trainer to set up your plan so the list is filtered.
            </div>
          )}

          {unitCodes.length === 0 && profile.role !== 'apprentice' && (
            <div style={{ padding: '6px 10px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 11, color: '#1e40af', marginBottom: 8 }}>
              💡 Could your evidence apply to multiple units? Select all relevant units to get the most out of each entry.
            </div>
          )}

          {/* Unit search */}
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input
              className="finp"
              placeholder="Search units by code or name…"
              value={unitSearch}
              onChange={e => setUnitSearch(e.target.value)}
              style={{ paddingLeft: 32, fontSize: 12 }}
            />
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'var(--muted)', pointerEvents: 'none' }}>🔍</span>
            {unitSearch && (
              <button
                onClick={() => setUnitSearch('')}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 14, color: 'var(--muted)', cursor: 'pointer', padding: '0 4px' }}
              >×</button>
            )}
          </div>

          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg)' }}>
            {/* Selected units always show at top */}
            {unitCodes.map(code => {
              const u = allUnits.find(u => u.code === code);
              if (!u) return null;
              const hasGuide = u.evidence_requirements || u.photo_guide || u.write_guide || u.example_entry || u.workshop_tip;
              return (
                <label key={u.code} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                  cursor: 'pointer', borderBottom: '1px solid var(--border)',
                  background: 'rgba(245,166,35,.08)',
                  fontSize: 12,
                }}>
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => toggleUnit(u.code)}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  <span style={{ fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-d)', letterSpacing: .5, minWidth: 85, fontSize: 11 }}>
                    {u.code}
                  </span>
                  <span style={{ color: 'var(--text)', flex: 1, fontSize: 11.5 }}>{u.name}</span>
                  {hasGuide && (
                    <button
                      className="btn-sm"
                      style={{ padding: '1px 6px', fontSize: 9 }}
                      onClick={e => { e.preventDefault(); setShowReqs(showReqs === u.code ? null : u.code); }}
                    >
                      {showReqs === u.code ? '▾' : '▸'} Guide
                    </button>
                  )}
                </label>
              );
            })}

            {/* Divider if there are selected units and filtered results */}
            {unitCodes.length > 0 && filteredUnits.length > 0 && (
              <div style={{ padding: '3px 10px', fontSize: 9, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.5, background: 'var(--panel)', borderBottom: '1px solid var(--border)' }}>
                {unitSearch ? `SEARCH RESULTS (${filteredUnits.length})` : 'ALL UNITS'}
              </div>
            )}

            {/* Filtered unselected units */}
            {filteredUnits.map(u => {
              const hasGuide = u.evidence_requirements || u.photo_guide || u.write_guide || u.example_entry || u.workshop_tip;
              return (
                <label key={u.code} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                  cursor: 'pointer', borderBottom: '1px solid var(--border)',
                  background: 'transparent',
                  fontSize: 12,
                }}>
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => toggleUnit(u.code)}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  <span style={{ fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-d)', letterSpacing: .5, minWidth: 85, fontSize: 11 }}>
                    {u.code}
                  </span>
                  <span style={{ color: 'var(--text)', flex: 1, fontSize: 11.5 }}>{u.name}</span>
                  {hasGuide && (
                    <button
                      className="btn-sm"
                      style={{ padding: '1px 6px', fontSize: 9 }}
                      onClick={e => { e.preventDefault(); setShowReqs(showReqs === u.code ? null : u.code); }}
                    >
                      {showReqs === u.code ? '▾' : '▸'} Guide
                    </button>
                  )}
                </label>
              );
            })}

            {/* No results message */}
            {filteredUnits.length === 0 && unitCodes.length === 0 && (
              <div style={{ padding: '14px 10px', textAlign: 'center', fontSize: 11, color: 'var(--muted)' }}>
                No units match "{unitSearch}"
              </div>
            )}
            {filteredUnits.length === 0 && unitCodes.length > 0 && unitSearch && (
              <div style={{ padding: '10px', textAlign: 'center', fontSize: 11, color: 'var(--muted)' }}>
                No additional units match "{unitSearch}"
              </div>
            )}
          </div>

          {/* Progress indicators */}
          {unitCodes.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {unitCodes.map(code => {
                const prog = getUnitProgress(code);
                return (
                  <div key={code} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px',
                    borderRadius: 4, fontSize: 11,
                    background: prog.met ? 'rgba(39,174,96,.08)' : 'rgba(245,166,35,.06)',
                    border: prog.met ? '1px solid rgba(39,174,96,.2)' : '1px solid rgba(245,166,35,.2)',
                  }}>
                    <span style={{ fontWeight: 700, fontFamily: 'var(--font-d)', color: 'var(--accent)', minWidth: 80 }}>{code}</span>
                    <span style={{ color: prog.met ? 'var(--green)' : 'var(--text)' }}>
                      {prog.approved}/{prog.minEv} approved
                    </span>
                    {prog.pending > 0 && <span style={{ color: 'var(--muted)' }}>({prog.pending} pending)</span>}
                    {prog.met && <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓ Met</span>}
                    {!prog.met && <span style={{ color: 'var(--muted)' }}>— {prog.minEv - prog.approved} more needed</span>}
                  </div>
                );
              })}
            </div>
          )}

          {unitCodes.length > 1 && (
            <div className="infobox" style={{ marginTop: 8, fontSize: 11 }}>
              This entry will count toward <strong>{unitCodes.length} units</strong>: {unitCodes.join(', ')}.
            </div>
          )}
        </div>

        {/* Evidence guide */}
        {showReqs && (() => {
          const gu = allUnits.find(u => u.code === showReqs);
          if (!gu) return null;
          const hasReqs = gu.evidence_requirements;
          const hasPhoto = gu.photo_guide;
          const hasWrite = gu.write_guide;
          const hasExample = gu.example_entry;
          const hasTip = gu.workshop_tip;
          if (!(hasReqs || hasPhoto || hasWrite || hasExample || hasTip)) return null;

          return (
            <div className="card" style={{ marginBottom: 14, borderLeft: '3px solid var(--accent)' }}>
              <div className="card-hdr" style={{ padding: '8px 12px' }}>
                <span className="card-title" style={{ fontSize: 12 }}>{gu.code} — Evidence Guide</span>
                <button className="btn-sm" style={{ fontSize: 9, padding: '1px 6px' }} onClick={() => setShowReqs(null)}>Close</button>
              </div>
              <div style={{ padding: 12 }}>
                {hasReqs && (
                  <div style={{ marginBottom: (hasPhoto || hasWrite || hasExample || hasTip) ? 10 : 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--head)', marginBottom: 4 }}>Requirements</div>
                    <EvidenceRequirements text={gu.evidence_requirements} />
                  </div>
                )}
                {hasPhoto && (
                  <div style={{ marginBottom: (hasWrite || hasExample || hasTip) ? 10 : 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--head)', marginBottom: 4 }}>📸 What to Photo</div>
                    <div style={{ fontSize: 11, lineHeight: 1.7, color: 'var(--text)' }}>
                      {gu.photo_guide.split(',').map((item, i) => (
                        <div key={i} style={{ display: 'flex', gap: 5, marginBottom: 1 }}>
                          <span style={{ color: 'var(--accent)', flexShrink: 0 }}>📷</span>
                          <span>{item.trim()}</span>
                        </div>
                      ))}
                    </div>
                    {gu.evidence_count_tip && (
                      <div style={{ marginTop: 6, padding: '4px 8px', borderRadius: 4, background: '#fffbeb', border: '1px solid #fde68a', fontSize: 10, color: '#92400e' }}>
                        💡 <strong>How many?</strong> {gu.evidence_count_tip}
                      </div>
                    )}
                  </div>
                )}
                {hasWrite && (
                  <div style={{ marginBottom: (hasExample || hasTip) ? 10 : 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--head)', marginBottom: 4 }}>✏️ What to Write</div>
                    <div style={{ fontSize: 11, lineHeight: 1.7, color: 'var(--text)' }}>{gu.write_guide}</div>
                  </div>
                )}
                {hasExample && (
                  <div style={{ marginBottom: hasTip ? 10 : 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--head)', marginBottom: 4 }}>📋 Example</div>
                    <div style={{ padding: '6px 8px', borderRadius: 4, background: 'var(--bg2)', border: '1px solid var(--border)', fontSize: 11, lineHeight: 1.7, fontStyle: 'italic', color: 'var(--text)' }}>
                      "{gu.example_entry}"
                    </div>
                  </div>
                )}
                {hasTip && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--head)', marginBottom: 4 }}>💡 Workshop Tip</div>
                    <div style={{ fontSize: 11, lineHeight: 1.7, color: 'var(--text)' }}>{gu.workshop_tip}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── STEP 3: Describe the work (structured) ── */}
        <div style={{ padding: '10px 14px', borderRadius: 6, background: 'var(--panel)', border: '1px solid var(--border)', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, letterSpacing: 0.5 }}>STEP 3 — DESCRIBE THE WORK</div>

          <div className="fgrp" style={{ marginBottom: 10 }}>
            <label className="flbl">What was the fault or reason for the work? *</label>
            <input
              className="finp"
              placeholder="e.g. Customer reported grinding noise from front brakes"
              value={faultReason}
              onChange={e => setFaultReason(e.target.value)}
            />
          </div>

          <div className="fgrp" style={{ marginBottom: 10 }}>
            <label className="flbl">What did you do? *</label>
            <textarea
              className="ftxt"
              placeholder="e.g. Removed front wheels, inspected brake pads and rotors. Pads worn below minimum thickness. Replaced pads and machined rotors on both sides."
              value={whatDid}
              onChange={e => setWhatDid(e.target.value)}
              rows={3}
            />
          </div>

          <div className="fgrp" style={{ marginBottom: 10 }}>
            <label className="flbl">What tools / equipment did you use?</label>
            <input
              className="finp"
              placeholder="e.g. Impact gun, torque wrench (120Nm), brake micrometer, brake lathe"
              value={toolsUsed}
              onChange={e => setToolsUsed(e.target.value)}
            />
          </div>

          <div className="fgrp" style={{ marginBottom: 0 }}>
            <label className="flbl">What was the outcome? *</label>
            <textarea
              className="ftxt"
              placeholder="e.g. New pads fitted and rotors machined to spec. Road tested — no noise, braking even on both sides. Vehicle returned to customer."
              value={outcome}
              onChange={e => setOutcome(e.target.value)}
              rows={2}
            />
          </div>

          {/* Live preview */}
          {(faultReason || whatDid || toolsUsed || outcome) && (
            <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 4, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 11, lineHeight: 1.7, color: 'var(--muted)' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--accent)', marginBottom: 4, letterSpacing: 0.5 }}>PREVIEW — HOW YOUR ENTRY WILL READ</div>
              {faultReason && <div><strong>Fault/Reason:</strong> {faultReason}</div>}
              {whatDid && <div><strong>Work performed:</strong> {whatDid}</div>}
              {toolsUsed && <div><strong>Tools/Equipment:</strong> {toolsUsed}</div>}
              {outcome && <div><strong>Outcome:</strong> {outcome}</div>}
            </div>
          )}
        </div>

        {/* ── STEP 4: Photos ── */}
        <div style={{ padding: '10px 14px', borderRadius: 6, background: 'var(--panel)', border: '1px solid var(--border)', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, letterSpacing: 0.5 }}>STEP 4 — PHOTOGRAPHIC EVIDENCE</div>
          <div style={{ padding: '8px 11px', borderRadius: 6, background: '#fef3c7', border: '1px solid #fde68a', fontSize: 11.5, color: '#92400e', fontWeight: 600, marginBottom: 6 }}>
            📸 Your face must be visible in all photos and videos — evidence without your face cannot be accepted.
          </div>
          <div style={{ padding: '8px 11px', borderRadius: 6, background: 'rgba(192,57,43,.1)', border: '1px solid rgba(192,57,43,.4)', fontSize: 11.5, color: '#7f1d1d', fontWeight: 700, marginBottom: 10 }}>
            🦺 PPE must be on in every photo — <u>safety glasses at a minimum</u>, plus whatever
            else the job calls for. Evidence missing PPE may be rejected.
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
            Upload up to 30 photos/videos — include before, during, and after shots plus any readings, labels, or spec plates.
          </div>
          <PhotoUploader
            profileId={profile.id}
            photoList={photoList}
            setPhotoList={setPhotoList}
            previewList={previewList}
            setPreviewList={setPreviewList}
          />
        </div>

        {err && <div className="err">{err}</div>}

        <div className="modal-actions">
          <button className="btn-cancel" onClick={handleClose}>
            {isEditingSubmitted ? 'Cancel' : hasDraftableContent() ? 'Save Draft & Close' : 'Cancel'}
          </button>
          <button className="btn" onClick={save} disabled={saving}>
            {saving ? 'Saving…'
              : isStaffEdit ? 'Save Changes →'
              : isEditingSubmitted ? 'Save Changes & Resubmit →'
              : 'Submit Entry →'}
          </button>
        </div>
      </div>
    </div>
  );
}
